import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import { useAuthStore } from "@/store/authentication"
import { useSubscriptionStore } from "@/store/subscription"

/**
 * Whether a request nobody clicked for — the bell's poll, the release-notes load — may be sent now.
 *
 * Such requests must stay silent when there is nobody (or nothing) to ask for: without a token every
 * request 401s, and a locked-out tenant answers 402 on every feature endpoint — either would turn a
 * background request into an error generator. Not inside the Erklärung's frame either: that instance is
 * a picture of the product, not a session (see services/viewHelp/embed.ts).
 *
 * One guard for every such caller, so a new lockout rule cannot reach one of them and miss the other.
 */
export const mayRequestInBackground = () => {
  if (IS_VIEW_HELP_EMBED) return false
  const authStore = useAuthStore()
  if (!authStore.getToken() || !authStore.getTenant()) return false
  return !useSubscriptionStore().isLocked
}

/**
 * Calls [load] every [intervalMs] while at least one component asks for it, and again whenever the tab comes
 * back into view: a tab left in the background skips ticks in some browsers, so returning to it would show
 * what may be an interval out of date.
 *
 * Reference-counted on purpose: what polls sits in the layout, so every navigation unmounts one instance and
 * mounts another, and the two can overlap. A plain boolean guard would let the outgoing instance's stop()
 * clear the timer the incoming one had just decided not to re-create, leaving it frozen until a full reload.
 * Counting also keeps a second consumer (say an overview page) from stealing the timer.
 *
 * Module-level in the store that polls, not store state: a timer handle is not reactive data.
 *
 * What [load] sends should be `background: true`. The tab coming back into view is often a phone waking
 * up, and the request then goes out before the network is back: without the flag that is a snackbar
 * nobody asked for and a Sentry event with no defect behind it (LEWARA-FRONTEND-64).
 */
export const backgroundPoll = (load, intervalMs) => {
  let timer = null
  let subscribers = 0
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") load()
  }
  return {
    start() {
      subscribers += 1
      if (timer) return
      load()
      timer = setInterval(load, intervalMs)
      document.addEventListener("visibilitychange", onVisibilityChange)
    },
    /** Stops once the last subscriber is gone. */
    stop() {
      subscribers = Math.max(0, subscribers - 1)
      if (subscribers > 0 || !timer) return
      clearInterval(timer)
      timer = null
      document.removeEventListener("visibilitychange", onVisibilityChange)
    },
  }
}
